import * as moment from 'moment';

import {ReferralCommissionStatusEnum} from '@spa/api';

import {
    ComponentTypesEnum,
    UnspecifiedKeysEnum,
} from '../../../app/shared/enums';
import {
    ReportTableColumn,
    ReportTableConfigInterface,
} from '../../../app/shared/components';
import {FilterControlInterface} from '../../../app/shared/interfaces';
import {
    AutocompleteControl,
    BaseControl,
    DatepickerControl,
    SelectControl,
} from '../../../app/shared/classes';

import {environment} from '../../../environments/environment';

/**
 * Filter control names of the Referral & Commission report. They are form control names only -
 * the request is mapped field by field in the component, so they are free to stay camelCase while
 * the wire stays snake_case.
 */
export const ReferralCommissionReportAffiliateSelector = 'referralCommissionAffiliate';
export const ReferralCommissionReportDateTypeSelector = 'referralCommissionDateType';
export const ReferralCommissionReportDateFromSelector = 'referralCommissionDateFrom';
export const ReferralCommissionReportDateToSelector = 'referralCommissionDateTo';
export const ReferralCommissionReportStatusSelector = 'referralCommissionStatus';
export const ReferralCommissionReportCountrySelector = 'referralCommissionCountryId';
export const ReferralCommissionReportQuickSearchSelector = 'referralCommissionQuickSearch';

/**
 * Server defaults of `POST referral/reports/referral-commissions` - the default order column is
 * `registration_date` (newest first) and the default page size is 50 (server accepts 1-200).
 */
export const ReferralCommissionReportDefaultOrderColumn = 'registration_date';
export const ReferralCommissionReportDefaultPageSize = 50;

/**
 * Deep-link query parameter carrying a numeric referral profile id. When present on navigation
 * the report pre-filters by it (`affiliate_profile_ids`) and auto-submits.
 */
export const ReferralCommissionReportAffiliateQueryParam = 'affiliate';

/** Largest page size the report endpoint accepts - used for the CSV export request. */
export const ReferralCommissionReportMaxPageSize = 200;

/**
 * Which commission date column the Date From/To pair filters. The values are the wire order
 * columns of the matching date range, so they double as the request field selectors.
 */
export enum ReferralCommissionReportDateTypesEnum {
    RegistrationDate = 'registration_date',
    PurchaseDate = 'purchase_date'
}

/**
 * Column selectors - the snake_case field names of ReferralCommissionReportRowInterface.
 */
export const ReferralCommissionReportColumnNames = {
    Affiliate: 'affiliate_email',
    ReferralCode: 'referral_code',
    ReferredUser: 'referred_email',
    RegistrationDate: 'registration_date',
    AcquisitionType: 'acquisition_type',
    PurchaseMade: 'purchase_made',
    PurchaseDate: 'purchase_date',
    Product: 'product',
    AmountPaid: 'amount_paid',
    Currency: 'currency',
    CommissionPercent: 'commission_percent',
    CommissionAmount: 'commission_amount',
    AmountKeptByAce: 'amount_kept_by_ace',
    CommissionStatus: 'commission_status',
    PayoutRequestId: 'payout_request_id'
};

/**
 * Filter block of the report. The two datepickers open pre-filled with today as a DRAFT only -
 * the page loads the full list first and the drafts are applied once the operator submits, the
 * same draft/applied split the referred-accounts report uses.
 */
export const PanelReportsReferralCommissionsFilterControls = (): FilterControlInterface[] => {
    const today = moment().format(environment.dateFormatLocalDate);

    return [
        {
            name: ReferralCommissionReportAffiliateSelector,
            placeholder: 'Affiliate (code or email)',
            controls: [
                new BaseControl({
                    name: ReferralCommissionReportAffiliateSelector,
                    value: null,
                    placeholder: 'Affiliate (code or email)',
                    validators: [],
                    componentType: ComponentTypesEnum.Text
                })
            ],
            shown: true,
            sortIndex: 1
        },
        {
            name: ReferralCommissionReportDateTypeSelector,
            placeholder: 'Date Type',
            controls: [
                new SelectControl({
                    name: ReferralCommissionReportDateTypeSelector,
                    value: ReferralCommissionReportDateTypesEnum.RegistrationDate,
                    placeholder: 'Date Type',
                    validators: [],
                    componentType: ComponentTypesEnum.Select,
                    options: [
                        {label: 'Registration Date', value: ReferralCommissionReportDateTypesEnum.RegistrationDate},
                        {label: 'Purchase Date', value: ReferralCommissionReportDateTypesEnum.PurchaseDate}
                    ]
                }),
                new DatepickerControl({
                    name: ReferralCommissionReportDateFromSelector,
                    value: today,
                    placeholder: 'Date From',
                    maxDate: today,
                    validators: [],
                    componentType: ComponentTypesEnum.Datepicker,
                    sortIndex: 2
                }),
                new DatepickerControl({
                    name: ReferralCommissionReportDateToSelector,
                    value: today,
                    placeholder: 'Date To',
                    maxDate: today,
                    validators: [],
                    componentType: ComponentTypesEnum.Datepicker,
                    sortIndex: 3
                })
            ],
            shown: true,
            sortIndex: 2
        },
        {
            name: ReferralCommissionReportStatusSelector,
            placeholder: 'Commission Status',
            controls: [
                new SelectControl({
                    name: ReferralCommissionReportStatusSelector,
                    value: UnspecifiedKeysEnum.UnspecifiedKey,
                    placeholder: 'Commission Status',
                    validators: [],
                    componentType: ComponentTypesEnum.Select,
                    options: [
                        {label: 'All', value: UnspecifiedKeysEnum.UnspecifiedKey},
                        {label: 'Unpaid', value: ReferralCommissionStatusEnum.Unpaid},
                        {label: 'Requested', value: ReferralCommissionStatusEnum.Requested},
                        {label: 'Paid', value: ReferralCommissionStatusEnum.Paid}
                    ]
                })
            ],
            shown: true,
            sortIndex: 3
        },
        {
            name: ReferralCommissionReportCountrySelector,
            placeholder: 'Referred User Country',
            controls: [
                new AutocompleteControl({
                    name: ReferralCommissionReportCountrySelector,
                    value: null,
                    placeholder: 'Referred User Country',
                    validators: [],
                    componentType: ComponentTypesEnum.Autocomplete,
                    // Country options are populated in the resolver from the countries list.
                    options: []
                })
            ],
            shown: true,
            sortIndex: 4
        },
        {
            name: ReferralCommissionReportQuickSearchSelector,
            placeholder: 'Quick Search',
            controls: [
                new BaseControl({
                    name: ReferralCommissionReportQuickSearchSelector,
                    value: null,
                    placeholder: 'Quick Search',
                    validators: [],
                    componentType: ComponentTypesEnum.Text
                })
            ],
            shown: true,
            sortIndex: 5
        }
    ];
};

/**
 * One row per referred user - purchaser or not. The Payout Request ID cell is rendered clickable
 * by the component whenever `payout_request_id` is present and opens the payout request detail.
 */
export const PanelReportsReferralCommissionsDataTableConfig = (): ReportTableConfigInterface => {
    return {
        rows: [],
        columns: [
            new ReportTableColumn({
                label: 'Affiliate',
                selector: ReferralCommissionReportColumnNames.Affiliate
            }),
            new ReportTableColumn({
                label: 'Referral Code',
                selector: ReferralCommissionReportColumnNames.ReferralCode
            }),
            new ReportTableColumn({
                label: 'Referred User',
                selector: ReferralCommissionReportColumnNames.ReferredUser
            }),
            new ReportTableColumn({
                label: 'Registration Date',
                selector: ReferralCommissionReportColumnNames.RegistrationDate
            }),
            new ReportTableColumn({
                label: 'Acquisition Type',
                selector: ReferralCommissionReportColumnNames.AcquisitionType
            }),
            new ReportTableColumn({
                label: 'Purchase Made',
                selector: ReferralCommissionReportColumnNames.PurchaseMade
            }),
            new ReportTableColumn({
                label: 'Purchase Date',
                selector: ReferralCommissionReportColumnNames.PurchaseDate
            }),
            new ReportTableColumn({
                label: 'Product',
                selector: ReferralCommissionReportColumnNames.Product
            }),
            new ReportTableColumn({
                label: 'Amount Paid',
                selector: ReferralCommissionReportColumnNames.AmountPaid,
                shifted: true
            }),
            new ReportTableColumn({
                label: 'Currency',
                selector: ReferralCommissionReportColumnNames.Currency,
                shifted: true
            }),
            new ReportTableColumn({
                label: 'Commission %',
                selector: ReferralCommissionReportColumnNames.CommissionPercent,
                shifted: true
            }),
            new ReportTableColumn({
                label: 'Commission',
                selector: ReferralCommissionReportColumnNames.CommissionAmount,
                shifted: true
            }),
            new ReportTableColumn({
                label: 'ACE Keeps',
                selector: ReferralCommissionReportColumnNames.AmountKeptByAce,
                shifted: true
            }),
            new ReportTableColumn({
                label: 'Commission Status',
                selector: ReferralCommissionReportColumnNames.CommissionStatus
            }),
            new ReportTableColumn({
                label: 'Payout Request ID',
                selector: ReferralCommissionReportColumnNames.PayoutRequestId,
                shifted: true
            })
        ],
        hiddenColumnIds: [],
        actionMenu: false,
        disableColumnsOptions: true
    };
};
